<template>
  <v-data-table
    :fixed-header="false"
    :height="undefined"
    v-if="houseId"
    v-bind="dataTableAttrs"
    :headers="headers"
    :items="list"
    :loading="isLoading"
    :options.sync="options"
    :server-items-length="total"
    :items-per-page.sync="itemsPerPage"
    v-model="selectedItems"
    @update:items-per-page="handleItemsPerPageUpdated"
  >
    <template v-slot:top>
      <rs-confirm
        ref="confirmDelete"
        :loading="isLoading"
        @confirmed="handleDeleteConfirmed"
        @cancelled="$refs.confirmDelete.hide()"
      />

      <AutoAssessmentForm
        @saved="loadList()"
        :house="originalData"
        ref="autoAssessmentForm"
      />

      <rs-table-title-bar
        class="mt-6"
        v-if="$refs.headSearch"
        title="Otomatik Borçlandırma"
        icon=""
        @clear-filters="$refs.headSearch.reset"
        @reload="loadList"
        show-delete
        :edit-enabled="selectedItems.length === 1"
        :delete-enabled="selectedItems.length > 0"
        :search.sync="search.query"
        add-route="#"
        @click:add="handleAddClick"
        @click:edit="handleEditClick"
        @click:delete="handleBatchDeleteClick"
        ref="titleBar"
      />
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:body.prepend="{ headers }">
      <rs-table-head-search
        :headers="headers"
        :search.sync="search"
        ref="headSearch"
      />
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:item.edit="{ item }">
      <a
        href="#"
        class="btn btn-icon btn-sm btn-clean"
        @click.prevent="handleEditClick(item)"
      >
        <i class="menu-icon mdi mdi-pencil"></i>
      </a>
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:item.resident="{ item }">
      <router-link
        :to="{
          name: 'definitions.residents.edit',
          params: { id: item.resident_id },
        }"
      >
        {{ item.resident }}
      </router-link>
    </template>

    <!-- eslint-disable-next-line -->
    <template v-slot:item.income_type_id="{ item }">
      {{ item.income_type }}
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.is_active="{ value }">
      <rs-table-cell-boolean :value="value" />
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.amount_per_house="{ value }">
      <rs-table-cell-number price :value="value" />
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.description="{ value }">
      <rs-table-cell-string :limit="20" :value="value" />
    </template>
    <!-- eslint-disable -->
    <template
      v-slot:item.last_payment_auto_assessment_timing_type_id="{ item }"
    >
      <!-- eslint-enable -->
      <span :title="item.last_payment_day || null">
        {{ item.last_payment_auto_assessment_timing_type }}
      </span>
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.issue_auto_assessment_timing_type_id="{ item }">
      <span :title="item.issue_day || null">
        {{ item.issue_auto_assessment_timing_type }}
      </span>
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.starts_on="{ value }">
      <rs-table-cell-date :value="value" />
    </template>
    <!-- eslint-disable-next-line -->
    <template v-slot:item.ends_on="{ value }">
      <rs-table-cell-date :value="value" />
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";

import { debounce } from "@/core/plugins/debounce";
import { hasDataTable } from "@/view/mixins";
import AutoAssessmentForm from "@/view/content/houses/AutoAssessmentForm";

export default {
  name: "HousePeriodicalAssessmentList",
  mixins: [hasDataTable],
  components: {
    AutoAssessmentForm,
  },
  props: {
    houseId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    ...mapGetters(["incomeTypeList", "autoAssessmentTimingTypeList"]),
  },
  watch: {
    options: {
      handler() {
        this.loadList();
      },
      deep: true,
    },
    search: {
      handler: debounce(function () {
        this.loadList();
      }),
      deep: true,
    },
    houseId() {
      this.loadList();
    },
  },
  data() {
    return {
      options: {
        sortBy: ["created_at"],
        sortDesc: [true],
      },
      headers: [
        {
          text: "",
          value: "edit",
          searchable: false,
          sortable: false,
          width: "30px",
          align: "center",
        },
        {
          text: this.$t("labels.debt_type"),
          value: "income_type_id",
          searchable: "select",
          options: () => this.incomeTypeList,
        },
        {
          text: this.$t("labels.description"),
          value: "description",
          searchable: "text",
        },
        {
          text: this.$t("labels.debtor_name"),
          value: "resident",
          searchable: "text",
        },
        {
          text: this.$t("labels.amount"),
          value: "amount_per_house",
          searchable: "number",
          align: "end",
        },
        {
          text: "Başlangıç Tarihi",
          value: "starts_on",
          searchable: "date",
        },
        {
          text: "Bitiş Tarihi",
          value: "ends_on",
          searchable: "date",
        },
        {
          text: this.$t("labels.status"),
          value: "is_active",
          searchable: "yesNo",
          align: "center",
        },
        {
          text: this.$t("labels.assessment_timing_type"),
          value: "issue_auto_assessment_timing_type_id",
          searchable: "multiselect",
          options: () => this.autoAssessmentTimingTypeList,
        },
        {
          text: this.$t("labels.last_payment_timing_type"),
          value: "last_payment_auto_assessment_timing_type_id",
          searchable: "multiselect",
          options: () => this.autoAssessmentTimingTypeList,
        },
      ],
    };
  },
  methods: {
    loadList() {
      if (this.isLoading || !this.houseId) {
        return;
      }

      const params = Object.assign(
        this.buildSearchParams(this.search, this.options)
      );
      params.house_id = this.houseId;
      this.resetTable();
      this.isLoading = true;

      this.$api
        .query("income/periodical-assessments", { params })
        .then((response) => {
          if (response.data && response.data.data) {
            this.list = response.data.data;
          }

          if (response.data && response.data.meta) {
            this.total = response.data.meta.total;
            this.options.page = response.data.meta.current_page;
            this.itemsPerPage = response.data.meta.per_page;
            this.options.itemsPerPage = this.itemsPerPage;
            this.footTotals = response.data.meta.totals;
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => (this.isLoading = false));
    },
    handleAddClick() {
      this.$refs.autoAssessmentForm.show(null, {
        house_id: this.houseId,
      });
    },
    handleEditClick(item) {
      if (item.id) {
        this.$refs.autoAssessmentForm.show(item.id);
      } else {
        this.$refs.autoAssessmentForm.show(this.selectedItems[0].id);
      }
    },
    handleBatchDeleteClick() {
      this.$refs.confirmDelete.show(
        `${this.selectedItems.length} tane otomatik borçlandırmayı silmek istediğinizden emin misiniz?`
      );
    },
    handleDeleteConfirmed() {
      if (this.isLoading) return false;

      this.isLoading = true;
      const promises = [];

      for (const index in this.selectedItems) {
        const promise = this.$api.delete(
          `income/periodical-assessments/${this.selectedItems[index].id}`
        );
        promises.push(promise);
      }

      Promise.all(promises)
        .then(() => {
          this.$toast.success("Seçilen otomatik borçlandırmalar silindi");
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.$refs.confirmDelete.hide();
          this.isLoading = false;
          this.loadList();
        });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.loadList();
    });
  },
};
</script>
